// New Trainee rules. Words: NEW_TRAINEE_WORDS.md (approved 2026-10-06).
// An admin adds a trainee from the Training Portal. Two ways to pick the tag:
// scan it, or take the next free tag at the site. No typed IDs.
// Shared by the portal screen and the server steps, and unit tested.
// Nothing here reads or writes the database.

/** What the tag check returns before the form opens. */
export type TraineeTagCheck =
  | { kind: "ok"; tagId: string; siteName: string; sessionCode: string | null }
  | { kind: "refused"; tagId: string; message: string }
  | { kind: "noneFree"; message: string };

/** "Springfield, KY" -> "Springfield". */
export function shortSiteName(name: string, siteNumber: string): string {
  const first = name.split(",")[0]?.trim();
  return first || `Site ${siteNumber}`;
}

/** The number on a QEW tag: 01-QEW15 -> 15. Null for anything else. */
export function tagNumber(tagId: string): number | null {
  const m = /^\d{2}-QEW(\d{2})$/.exec(tagId);
  return m ? Number(m[1]) : null;
}

/**
 * The lowest unclaimed QEW tag at the site. Contractor tags are left out.
 * `taken` holds tag IDs that already have a person record, so they are skipped too.
 */
export function nextFreeTag(unclaimedIds: readonly string[], siteNumber: string, taken: ReadonlySet<string> = new Set()): string | null {
  const free = unclaimedIds
    .filter((id) => id.startsWith(`${siteNumber}-QEW`) && tagNumber(id) !== null && !taken.has(id))
    .sort((a, b) => tagNumber(a)! - tagNumber(b)!);
  return free[0] ?? null;
}

/**
 * Why a tag cannot take a new trainee. Null when it can.
 * Approved refusal lines, MOCKUP_NEW_TRAINEE_APPROVED.html screen 5.
 */
export function traineeTagProblem(
  tagId: string,
  siteNumber: string,
  siteNames: ReadonlyMap<string, string>,
  badge: Record<string, unknown> | null,
  personName: string | null,
): string | null {
  if (tagNumber(tagId) === null) return "That is not a QEW bag tag. Scan the trainee's bag tag.";
  const tagSite = tagId.slice(0, 2);
  if (tagSite !== siteNumber) {
    const theirs = shortSiteName(siteNames.get(tagSite) ?? "", tagSite);
    const ours = shortSiteName(siteNames.get(siteNumber) ?? "", siteNumber);
    return `${tagId} is a ${theirs} tag. This class is at ${ours}.`;
  }
  if (!badge) return `${tagId} is not in the system. Check the number on the tag.`;
  const status = String(badge.Status ?? "");
  if (status === "Disabled") return `${tagId} is out of service. Pick another tag.`;
  if (status !== "Unclaimed") {
    const holder = String(badge["Holder Name"] ?? "").trim() || personName || "someone";
    return `${tagId} already belongs to ${holder}. Pick another tag.`;
  }
  if (personName !== null) return `${tagId} already belongs to ${personName || "someone"}. Pick another tag.`;
  return null;
}

/** The green line after saving. Approved wording, screen 6. */
export function addedLine(holderName: string, tagId: string, lotoReported: boolean): string {
  return `${holderName} added on ${tagId}. ${lotoReported ? "LOTO date sent for verification." : "No LOTO date on file yet."}`;
}
